import { SignUpStepType, SellerDetails } from '@zonofi/zo-types';
import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  useLazyValidateGSTDetailsQuery,
  useCreateSellerMutation,
  useRegenerateTokenMutation,
  getToken,
  getRefreshToken,
  useAppSelector,
  JTBDEventType,
  authenticate,
  dispatch,
  useEventTracking,
  useLazyGetWorkspaceQuery,
} from '@zonofi/seller-rtk-store';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import moment from 'moment';
import { getConfigValue } from '@zonofi/common/functions/get-config-value/get-config-value';
import { AGENT_SETUP_ROUTE } from '../navigation';
import { CountryObject } from '@zonofi/i18n';

export const useLoginSignupScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname; // e.g., "/signup/OTP"

  const [sellerDetails, setSellerDetails] = useState<SellerDetails>({
    phoneNumber: '',
    gstin: '',
    tradeName: '',
    companyName: '',
    address: '',
    city: '',
    state: '',
    pinCode: '',
    contactName: '',
    emailId: '',
    category: undefined,
    industry: undefined,
    gstValidTill: '',
    planCode: '',
    isEmail: false,
    selectedPlan: '',
    utmSource: '',
    utmCampaign: '',
    utmCampaignId: '',
    x_ad_id: '',
    x_fclid: '',
    x_gclid: '',
    utmMedium: '',
    utmContent: '',
    utmId: '',
    utmTerm: '',
    referenceLink: '',
    dial_code: '+91',
    selectedCountry: {
      name: 'India',
      flag: '🇮🇳',
      code: 'IN',
      dial_code: '+91',
      currency_code: 'INR',
      time_zones: ['Asia/Kolkata'],
    },
  });

  const [gstError, setGstError] = useState<string>('');
  const [, setSearchParams] = useSearchParams();

  const [step, setStep] = useState<SignUpStepType>('PHONE');

  const [errorObj, setErrorObj] = useState<{
    isError: boolean;
    errorMsg: string;
  }>({
    isError: false,
    errorMsg: '',
  });

  const [validateGSTAPI, validateGSTAPIResponse] =
    useLazyValidateGSTDetailsQuery();
  const [tagIds, setTagIds] = useState([11, 13]);

  const [registerSellerAPI, registerSellerAPIResponse] =
    useCreateSellerMutation();
  const [regenerateTokenAPI, regenerateTokenAPIResponse] =
    useRegenerateTokenMutation();
  const [fetchWorkspacesReq, fetchWorkspacesRes] = useLazyGetWorkspaceQuery();

  const { trackEvent } = useEventTracking();
  const stepFromPath = useMemo(() => path.split('/').pop(), [path]);

  const onUpdateValue = (
    key: string,
    value: string | boolean | CountryObject
  ) => {
    setSellerDetails((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const goToStep = (step: SignUpStepType) => {
    setStep(step);
    setSearchParams({ step });
  };

  const resetData = () => {
    setSellerDetails((prev) => ({
      ...prev,
      ...(prev.isEmail ? { phoneNumber: '' } : { emailId: '' }),
      contactName: '',
      category: undefined,
      industry: undefined,
    }));
  };

  const validateGST = useCallback(() => {
    resetData();
    validateGSTAPI(sellerDetails?.gstin);
  }, [sellerDetails?.gstin]);

  const handleGoBackFromCompanyDetails = () => {
    navigate(-1);
    setSellerDetails((prev) => {
      return {
        ...prev,
        gstin: '',
        tradeName: '',
        companyName: '',
        address: '',
        city: '',
        state: '',
        pinCode: '',
        gstValidTill: '',
      };
    });
  };

  const onRegister = () => {
    const payload = {
      ...(sellerDetails?.gstin?.length !== 0
        ? { gstIn: sellerDetails?.gstin }
        : {}),
      ...(sellerDetails?.gstValidTill
        ? {
            gstValidityEndDate: moment(
              sellerDetails?.gstValidTill,
              'DD/MM/YYYY'
            ).format('YYYY-MM-DD'),
          }
        : {}),
      spaceName: sellerDetails?.companyName,
      companyName: sellerDetails?.tradeName || sellerDetails?.companyName,
      address: {
        address1: sellerDetails?.address,
        address2: '',
        city: sellerDetails?.city,
        state: sellerDetails?.state,
        pincode: sellerDetails?.pinCode,
      },
      firstName: sellerDetails?.contactName,
      companyEmail: sellerDetails?.emailId,
      businessCategory: sellerDetails?.category,
      mobile: `${sellerDetails?.phoneNumber}`,
      industry: sellerDetails?.industry,
      isoCountryCode: sellerDetails?.selectedCountry?.code,
      defaultCurrencyCode: sellerDetails?.selectedCountry?.currency_code,
      timeZone: sellerDetails?.selectedCountry?.time_zones[0],
      // planCode: 'OND001',
    };

    if (!payload.companyEmail) {
      delete payload.companyEmail;
    }
    registerSellerAPI(payload);
    // if (planCode === 'custom') {
    //   goToStep(SignUpStepEnum.CONTACT_US);
    // }
  };

  const isRegistering = useMemo(() => {
    return registerSellerAPIResponse?.isLoading;
  }, [registerSellerAPIResponse]);

  const handleLaunchApp = () => {
    regenerateTokenAPI({ token, refreshToken });
  };

  const googleScriptId = getConfigValue('GOOGLE_SCRIPT_ID');

  const regenerateTokenAPIFn = async () => {
    try {
      const response = await fetch(
        `https://script.google.com/macros/s/${googleScriptId}/exec`,
        {
          method: 'POST',
          mode: 'no-cors',
          headers: {
            'Content-Type': 'text/plain',
            'Access-Control-Allow-Origin': '*',
            'Access-Control-Allow-Methods': 'GET,POST,OPTIONS',
            'Access-Control-Allow-Headers': 'Content-Type',
          },
          body: JSON.stringify({
            keyCustomer: sellerDetails?.tradeName,
            firmName: sellerDetails?.companyName,
            vertical: 'Brand',
            city: sellerDetails?.city,
            reference_link: sellerDetails?.referenceLink,
            phoneNumber: sellerDetails?.phoneNumber,
            emailAddress: sellerDetails?.emailId,
            stage: 'S3',
            sequence: '1',
            utmSource: sellerDetails?.utmSource || '',
            utmMedium: sellerDetails?.utmMedium || '',
            utmCampaign: sellerDetails?.utmCampaign || '',
            tagIds: tagIds,
            extraFileds: {
              x_gclid: sellerDetails?.x_gclid,
              x_fclid: sellerDetails?.x_fclid,
              x_ad_id: sellerDetails?.x_ad_id,
              x_campaign_id:
                sellerDetails?.utmCampaign ||
                sellerDetails?.utmCampaignId ||
                '',
              x_content: sellerDetails?.utmContent,
              x_term: sellerDetails?.utmTerm,
              medium_id: sellerDetails?.utmId,
              reference_link: sellerDetails?.referenceLink,
            },
          }),
        }
      );

      const text = await response.json();
      console.log('Response from server:', text);
    } catch (error) {
      console.error('Error sending data:', error);
    }
    // console.log('regenerateToken333');
    // handleAppRedirection();
  };

  const isProduction = getConfigValue('APP_ENV') === 'PROD';

  useEffect(() => {
    const tagMap: Record<string, number[]> = {
      email: [11, 63], // Email Marketing
      facebook: [11, 64], // Facebook / Meta
      google: [65], // Google Ads
      google_ads: [65], // Google Ads (alias)
    };

    const emailSources = new Set([
      'email',
      'e-mail',
      'newsletter',
      'mailchimp',
      'constantcontact',
    ]);

    const facebookSources = new Set([
      'facebook',
      'instagram',
      'ig',
      'fb',
      'an',
      'messenger',
      'mg',
      'meta',
      'fbads',
      'insta',
      'igads',
    ]);

    const googleSources = new Set([
      'google',
      'google_ads',
      'gads',
      'adwords',
      'ga',
      'gclid',
    ]);
    const getTagIds = (utm_medium?: string, utm_source?: string): number[] => {
      const source = utm_source?.toLowerCase();
      const medium = utm_medium?.toLowerCase();

      if (source && emailSources.has(source)) return tagMap['email'];
      if (source && facebookSources.has(source)) {
        return tagMap['facebook'];
      }
      if (source && googleSources.has(source)) return tagMap['google'];

      return tagMap[medium ?? ''] || tagMap[source ?? ''] || [11]; // Default fallback
    };
    const tagIdsRes = getTagIds(
      sellerDetails?.utmMedium,
      sellerDetails?.utmSource
    );
    setTagIds(tagIdsRes);
  }, [sellerDetails?.utmMedium, sellerDetails?.utmSource]);

  useEffect(() => {
    if (registerSellerAPIResponse.isSuccess && registerSellerAPIResponse.data) {
      if (registerSellerAPIResponse?.data?.hostedPaymentPageUrl) {
        window.location.href =
          registerSellerAPIResponse?.data?.hostedPaymentPageUrl;
      } else {
        // finService.trackEvent('signup-complete', {
        //   user_id: userData?.id,
        //   email: userData?.email,
        //   name: userData?.firstName + ' ' + userData?.lastName,
        //   sellerWorkspaceId: workspaceId,
        // });
        trackEvent(JTBDEventType.SIGNUP_COMPLETE, { ...sellerDetails });
        // goToStep(SignUpStepEnum.LAUNCH);
        handleLaunchApp();
        if (isProduction) regenerateTokenAPIFn();
      }
    } else if (registerSellerAPIResponse.isError) {
      const error = registerSellerAPIResponse?.error as {
        data?: { message?: string };
      };
      const fullMessage = error?.data?.message;
      const match = fullMessage?.match(
        /(?:Error while creating workspace:\s*)+(.*)/
      );
      const finalMessage = match ? match[1] : fullMessage;
      setErrorObj({
        isError: true,
        errorMsg: finalMessage,
      });
      navigate(-1);
    }
  }, [registerSellerAPIResponse]);

  const closeErrorToast = () => {
    setErrorObj({
      isError: false,
      errorMsg: '',
    });
  };

  const token = useAppSelector(getToken);
  const refreshToken = useAppSelector(getRefreshToken);
  const encodedData = localStorage.getItem('encodedData');
  let newEncodedData = '';
  if (encodedData) {
    const decodedData = atob(encodedData);
    const decodedDataObj = JSON.parse(decodedData);
    decodedDataObj.token = token;
    decodedDataObj.refreshToken = refreshToken;
    decodedDataObj.userRoles = ['adm'];
    newEncodedData = btoa(JSON.stringify(decodedDataObj));
  }

  const handleAppRedirection = () => {
    const encodedData = localStorage.getItem('encodedData');

    // Get the base URL and ensure it doesn't end with a slash
    let baseUrl = getConfigValue('APP_ENDPOINT');
    if (baseUrl.endsWith('/')) {
      baseUrl = baseUrl.slice(0, -1);
    }

    if (encodedData) {
      window.location.href = `${baseUrl}/authenticate?data=${newEncodedData}`;
    } else {
      window.location.href = baseUrl;
    }
    localStorage.removeItem('encodedData');
  };

  useEffect(() => {
    if (regenerateTokenAPIResponse?.isSuccess) {
      // handleAppRedirection();
      fetchWorkspacesReq();
    }
  }, [regenerateTokenAPIResponse]);

  useEffect(() => {
    if (fetchWorkspacesRes?.isSuccess) {
      dispatch(authenticate());
      navigate(AGENT_SETUP_ROUTE.path);
    }
  }, [fetchWorkspacesRes]);

  useEffect(() => {
    if (validateGSTAPIResponse?.isSuccess) {
      console.log('validateGSTAPIResponse: ', validateGSTAPIResponse);
      if (validateGSTAPIResponse?.data) {
        setSellerDetails((prev) => {
          return {
            ...prev,
            companyName: validateGSTAPIResponse?.data?.firm_name,
            gstValidTill: validateGSTAPIResponse?.data?.valid_till,
            gstin: validateGSTAPIResponse?.data?.gstin_number,
            address: validateGSTAPIResponse?.data?.address?.st,
            city: validateGSTAPIResponse?.data?.address?.dst,
            state: validateGSTAPIResponse?.data?.address?.stcd,
            pinCode: validateGSTAPIResponse?.data?.address?.pncd,
            contactName: validateGSTAPIResponse?.data?.address?.bnm,
          };
        });
      }
      setGstError('');
      // goToStep(SignUpStepEnum.COMPANY_DETAILS);
      trackEvent(JTBDEventType.SIGNUP_GSTIN_PROVIDED, {
        companyName: validateGSTAPIResponse?.data?.firm_name,
        gstValidTill: validateGSTAPIResponse?.data?.valid_till,
        gstin: validateGSTAPIResponse?.data?.gstin_number,
        address: validateGSTAPIResponse?.data?.address?.st,
        city: validateGSTAPIResponse?.data?.address?.dst,
        state: validateGSTAPIResponse?.data?.address?.stcd,
        pinCode: validateGSTAPIResponse?.data?.address?.pncd,
        contactName: validateGSTAPIResponse?.data?.address?.bnm,
      });
      navigate('/companyDetails');
    } else if (validateGSTAPIResponse?.isError) {
      setGstError((validateGSTAPIResponse.error as any)?.data?.message);
    }
  }, [validateGSTAPIResponse]);

  return {
    navigate,
    stepFromPath,
    onUpdateValue,
    sellerDetails,
    goToStep,
    gstError,
    setGstError,
    validateGST,
    isValidatingGST: validateGSTAPIResponse?.isLoading,
    resetData,
    handleGoBackFromCompanyDetails,
    onRegister,
    isRegistering,
    errorObj,
    closeErrorToast,
    step,
    handleLaunchApp,
    isLaunching: regenerateTokenAPIResponse?.isLoading,
    setSellerDetails,
  };
};
